import { assertConfigSchema, type ConfigSchema } from './schema.js';
import { contractError, freezeJson, validateJsonObject, type JsonObject } from './json.js';
import type { GameplaySemantic } from './semantics.js';

export const GAMEPLAY_CONTRACT_VERSION = 1;
export const GAMEPLAY_CATALOG_VERSION = '1';
const number = (min = 0, max = 1_000_000): ConfigSchema => ({ type: 'number', min, max });
const integer = (min = 0, max = 1_000_000): ConfigSchema => ({ type: 'number', min, max, integer: true });
const id: ConfigSchema = { type: 'string', maxLength: 64, pattern: 'id' };
const enumeration = (...values: string[]): ConfigSchema => ({ type: 'string', maxLength: 64, values });
const bool: ConfigSchema = { type: 'boolean' };
const list = (items: ConfigSchema, maxItems = 64, minItems = 0, unique = false): ConfigSchema => ({ type: 'array', items, maxItems, minItems, unique });
const object = (properties: Record<string, ConfigSchema>, required = Object.keys(properties)): ConfigSchema => ({ type: 'object', properties, required });
const vector = object({ x: number(-1_000_000), y: number(-1_000_000) });
const ticks = integer(0, 10_000_000);
const positiveTicks = integer(1, 10_000_000);
const shape = { type: 'union', variants: [
  object({ type: enumeration('circle'), radius: number(0.000001) }),
  object({ type: enumeration('aabb'), w: number(0.000001), h: number(0.000001) }),
  object({ type: enumeration('capsule'), radius: number(0.000001), length: number(0.000001) }),
] } satisfies ConfigSchema;

/** All entries are schema skeletons. None is a runtime implementation or an
 * online certificate. Each facet names the invariant later certification owns. */
const multiplayer = freezeJson({
  authoritativeState: 'GameSim caller-owned state only',
  commands: { actor: 'canonical-slot', validation: 'sim-owned', ordering: 'before-fixed-step', rejectedActionsEmitNoOutcome: true },
  matchConfig: 'same immutable MatchConfigV1 for direct/local/server/replay',
  events: 'semantic actor/target/audience; host assigns match-scoped sequence',
  projection: 'host validates pure per-viewer projection before delivery; bots use permitted views',
  botBehavior: 'same validated command vocabulary as humans',
  reconnect: 'snapshot restores durable state; input epoch and bounded event cursor restore delivery',
  persistence: 'canonical slot-indexed scores/outcome; no client-authored results',
  localServerParity: 'exact configuration, ordered command tape, state/event/outcome hashes required',
  humanAgency: 'each declared human seat must pass independent attributable command probes',
  presentation: 'shell consumes canonical match state and semantic events only',
  mobile: 'same simulation; declared actions map to supported control profiles',
});

function entry(config: ConfigSchema, semantics: readonly GameplaySemantic[]) {
  return { version: 1, implementation: 'contracts-only' as const, onlineCertified: false as const,
    config, semantics, multiplayer, compatibility: { gameplayContract: GAMEPLAY_CONTRACT_VERSION, requiredOnlineProtocol: 2 } };
}

export const GAMEPLAY_CAPABILITY_CATALOG = freezeJson({
  'motion.body2d': entry(object({ axes: enumeration('x', 'y', 'xy'), acceleration: number(), braking: number(), drag: number(0, 1),
    maxSpeed: number(0.000001), turnRate: number(), gravity: vector, jumpImpulse: number(), groundedSteering: number(0, 1),
    airSteering: number(0, 1), quantization: number(0.000001, 1) }, ['axes', 'maxSpeed', 'quantization']), ['action.committed']),
  'collision.shape2d': entry(object({ shapes: list(object({ id, shape, layer: integer(0, 31), mask: integer(0, 4_294_967_295),
    body: enumeration('static', 'dynamic'), sweep: enumeration('discrete', 'swept') }), 256, 1) }), ['contact.entered']),
  'contact.impulse': entry(object({ restitution: number(0, 1), friction: number(0, 1), massClass: enumeration('light', 'normal', 'heavy'),
    contactCooldownTicks: ticks, resolutionOrder: enumeration('entity-id') }, ['restitution', 'friction', 'resolutionOrder']), ['contact.entered']),
  'projectile.lifecycle': entry(object({ spawnOffset: vector, speed: number(0.000001), lifetimeTicks: positiveTicks, cooldownTicks: ticks,
    maxLivePerOwner: integer(1, 1024), collisionMask: integer(0, 4_294_967_295), pierceCount: integer(0, 1024), bounceCount: integer(0, 1024),
    damage: number(), friendlyFire: bool }, ['speed', 'lifetimeTicks', 'cooldownTicks', 'maxLivePerOwner']),
  ['projectile.spawned', 'projectile.bounced', 'projectile.expired', 'hit.landed']),
  'combat.action': entry(object({ actions: list(object({ id, startupTicks: ticks, activeTicks: positiveTicks, recoveryTicks: ticks,
    cancelPolicy: enumeration('none', 'recovery', 'any'), hurtboxClass: id, damage: number(), stunTicks: ticks, knockback: vector,
    blockTicks: ticks, parryTicks: ticks, invulnerabilityTicks: ticks,
    hitboxes: list(object({ fromTick: ticks, toTick: ticks, shapeId: id }), 128) }, ['id', 'startupTicks', 'activeTicks', 'recoveryTicks', 'hitboxes']), 64, 1),
    simultaneousHitPolicy: enumeration('trade', 'actor-id') }), ['action.started', 'action.committed', 'action.rejected', 'hit.landed']),
  'combat.vitals': entry(object({ health: number(0.000001), lives: integer(1, 1000), invulnerabilityTicks: ticks, respawnTicks: ticks,
    downPolicy: enumeration('respawn', 'eliminate'), roundReset: enumeration('restore', 'carry') }, ['health', 'lives', 'downPolicy']), ['damage.applied', 'match.ended']),
  'objective.contested': entry(object({ pickupRadius: number(0.000001), claimTieBreak: enumeration('slot', 'entity-id'), possessionCooldownTicks: ticks,
    holdTicks: ticks, steal: bool, dropOnHit: bool, scoreValue: number(), outOfBounds: enumeration('reset', 'drop'),
    goals: list(object({ id, team: integer(0, 63), x: number(-1_000_000), y: number(-1_000_000), w: number(0.000001), h: number(0.000001) }), 64) },
  ['pickupRadius', 'claimTieBreak', 'possessionCooldownTicks', 'scoreValue', 'outOfBounds']), ['objective.changed', 'score.changed']),
  'service.order-flow': entry(object({ components: list(id, 256, 1, true),
    stations: list(object({ id, capacity: integer(1, 64), capabilities: list(id, 64, 1, true) }), 64, 1),
    recipes: list(object({ id, steps: list(object({ id, requires: list(id, 64, 0, true), station: id, operation: id,
      inputs: list(id, 64), output: id }), 128, 1) }), 64, 1),
    carryLimit: integer(1, 64), maxOrders: integer(1, 256), cadenceTicks: positiveTicks, patienceTicks: positiveTicks,
    score: number(), penalty: number(), simultaneousClaim: enumeration('slot', 'entity-id') }),
  ['order.created', 'order.progressed', 'order.served', 'order.expired', 'score.changed']),
  'match.rules': entry(object({ scoreTarget: number(0.000001), clock: object({ mode: enumeration('none', 'countup', 'countdown'), limitTicks: positiveTicks }, ['mode']),
    periods: integer(1, 1000), bestOf: integer(1, 999), lives: integer(1, 1000), tiePolicy: enumeration('draw', 'overtime', 'sudden-death'),
    timeoutOrdering: enumeration('score-before-timeout', 'timeout-before-score'), forfeitPolicy: enumeration('opposing-team', 'placement', 'no-contest') },
  ['clock', 'tiePolicy', 'timeoutOrdering', 'forfeitPolicy']), ['score.changed', 'round.started', 'round.ended', 'match.ended']),
});

export type GameplayCapabilityId = keyof typeof GAMEPLAY_CAPABILITY_CATALOG;
export const GAMEPLAY_CAPABILITY_IDS = Object.freeze(Object.keys(GAMEPLAY_CAPABILITY_CATALOG).sort() as GameplayCapabilityId[]);

/** Additive explicit versions keep the default v1 catalog and existing lock
 * identities unchanged. Replay rounds count decisive wins, not total rounds. */
export const MATCH_RULES_CAPABILITY_V2 = freezeJson({
  ...entry(object({
    scoreTarget: integer(1, 1),
    clock: object({ mode: enumeration('countdown'), limitTicks: integer(1800, 1800) }),
    bestOf: integer(1, 999),
    tiePolicy: enumeration('replay-round'),
    timeoutOrdering: enumeration('score-before-timeout'),
    maxMatchTicks: positiveTicks,
    budgetExhaustion: enumeration('no-contest'),
    forfeitPolicy: enumeration('no-contest'),
  }), ['score.changed', 'round.started', 'round.ended', 'match.ended']),
  version: 2,
});

/** Runtime helpers are deliberately registered separately from the capability
 * catalog. A selected capability remains a contract declaration; it does not
 * imply that a game imported, configured, or proved one of these modules. */
export interface ImplementedGameplayModuleV1 {
  id: string;
  version: number;
  exports: readonly string[];
  compatibleSimInterface: readonly ('createSim' | 'applyCommand' | 'step' | 'snapshotPayload' | 'scores' | 'status' | 'drainEvents')[];
  usageExample: string;
  testFiles: readonly string[];
  fixtureIds: readonly string[];
}

export const IMPLEMENTED_GAMEPLAY_MODULE_REGISTRY_V1 = Object.freeze([
  Object.freeze({ id: 'platform-run', version: 1,
    exports: Object.freeze(['createPlatformRunV1', 'courierShortConfigV1', 'validatePlatformRunConfigV1']),
    compatibleSimInterface: Object.freeze(['createSim', 'applyCommand', 'step', 'snapshotPayload', 'scores', 'status', 'drainEvents'] as const),
    usageExample: 'const loop = createPlatformRunV1(courierShortConfigV1()); loop.applyCommand(0, command); loop.step();',
    testFiles: Object.freeze(['packages/gameplay-kit/test/platformRun.test.ts']),
    fixtureIds: Object.freeze(['courier-short@1']),
  }),
  Object.freeze({ id: 'platformer-motion', version: 1,
    exports: Object.freeze(['createPlatformerMotionV1', 'createPlatformerMotionV2']),
    compatibleSimInterface: Object.freeze(['createSim', 'step', 'snapshotPayload'] as const),
    usageExample: "const step = createPlatformerMotionV2(config); const result = step(body, input, surfaces);",
    testFiles: Object.freeze(['packages/gameplay-kit/test/motionBody2d.test.ts', 'packages/gameplay-kit/test/motionBody2dV2.test.ts']),
    fixtureIds: Object.freeze(['platformer-solid-one-way@1']),
  }),
  Object.freeze({ id: 'collision-kinematic', version: 1,
    exports: Object.freeze(['sweepAabb2d', 'sweepMovingAabb2d', 'advanceKinematicBody2d']),
    compatibleSimInterface: Object.freeze(['createSim', 'step', 'snapshotPayload'] as const),
    usageExample: "const next = advanceKinematicBody2d(hazard, dt, bounds);",
    testFiles: Object.freeze(['packages/gameplay-kit/test/sweepAabb2d.test.ts', 'packages/gameplay-kit/test/kinematicBody2d.test.ts']),
    fixtureIds: Object.freeze(['area-per-entity-motion@1']),
  }),
] satisfies readonly ImplementedGameplayModuleV1[]);

export function getImplementedGameplayModule(id: string, version = 1): ImplementedGameplayModuleV1 {
  const selected = IMPLEMENTED_GAMEPLAY_MODULE_REGISTRY_V1.find((module) => module.id === id && module.version === version);
  if (!selected) contractError('implemented-module', `unsupported implemented module ${id}@${version}`);
  return selected;
}

export function getGameplayCapability(id: unknown, version: unknown = 1) {
  if (typeof id !== 'string' || !Object.hasOwn(GAMEPLAY_CAPABILITY_CATALOG, id)) contractError('capability.id', `unsupported capability ${String(id)}; supported: ${GAMEPLAY_CAPABILITY_IDS.join(', ')}`);
  const selected = GAMEPLAY_CAPABILITY_CATALOG[id as GameplayCapabilityId];
  if (id === 'match.rules' && version === 2) return MATCH_RULES_CAPABILITY_V2;
  if (version !== selected.version) contractError(`capability.${id}.version`, `unsupported version ${String(version)}; supported: ${id === 'match.rules' ? '1, 2' : selected.version}`);
  return selected;
}

function uniqueIds(values: JsonObject[], path: string): void {
  if (new Set(values.map(v => v.id)).size !== values.length) contractError(path, 'duplicate stable IDs');
}

/** Catalog schema + cross-field invariants, without instantiating gameplay. */
export function validateCapabilityConfig(id: GameplayCapabilityId, version: number, input: unknown): JsonObject {
  const config = validateJsonObject(input, `capability.${id}.config`);
  assertConfigSchema(config, getGameplayCapability(id, version).config, `capability.${id}.config`);
  if (id === 'collision.shape2d') uniqueIds(config.shapes as JsonObject[], 'shapes');
  if (id === 'combat.action') {
    const actions = config.actions as JsonObject[];
    uniqueIds(actions, 'actions');
    for (const action of actions) {
      for (const box of action.hitboxes as JsonObject[]) {
        if ((box.fromTick as number) > (box.toTick as number) || (box.toTick as number) >= (action.activeTicks as number)) {
          contractError(`actions.${action.id}.hitboxes`, 'hitbox window must be ordered and inside activeTicks');
        }
      }
    }
  }
  if (id === 'match.rules') {
    const clock = config.clock as JsonObject;
    if (clock.mode === 'countdown' && clock.limitTicks === undefined) contractError('clock.limitTicks', 'required for countdown');
    if (clock.mode === 'none' && clock.limitTicks !== undefined) contractError('clock.limitTicks', 'not allowed when clock mode is none');
    if (typeof config.bestOf === 'number' && config.bestOf % 2 === 0) contractError('bestOf', 'must be odd');
  }
  if (id === 'service.order-flow') {
    const stations = config.stations as JsonObject[];
    const recipes = config.recipes as JsonObject[];
    uniqueIds(stations, 'stations'); uniqueIds(recipes, 'recipes');
    const components = new Set(config.components as string[]);
    for (const recipe of recipes) {
      const steps = recipe.steps as JsonObject[];
      uniqueIds(steps, `recipes.${recipe.id}.steps`);
      const byId = new Map(steps.map(s => [s.id as string, s]));
      const visiting = new Set<string>(), visited = new Set<string>();
      function visit(stepId: string): void {
        const step = byId.get(stepId);
        if (!step) contractError(`recipes.${recipe.id}`, `unknown required step ${stepId}`);
        if (visiting.has(stepId)) contractError(`recipes.${recipe.id}`, 'recipe dependency cycle');
        if (visited.has(stepId)) return;
        visiting.add(stepId);
        for (const dependency of step.requires as string[]) visit(dependency);
        visiting.delete(stepId); visited.add(stepId);
      }
      for (const step of steps) {
        const station = stations.find(s => s.id === step.station);
        if (!station || !(station.capabilities as string[]).includes(step.operation as string)) contractError(`recipes.${recipe.id}.${step.id}`, 'station cannot perform declared operation');
        if (![...(step.inputs as string[]), step.output as string].every(c => components.has(c))) contractError(`recipes.${recipe.id}.${step.id}`, 'unknown component');
        visit(step.id as string);
      }
    }
  }
  return config;
}
